import { useLingui } from '@lingui/react/macro'
import { useClickOutside } from 'components/table/enhancements/filters/filterUtils'
import { Text } from 'designSystem/components/Text'
import React, { memo, useCallback, useMemo, useRef, useState } from 'react'

import {
  Checkbox,
  CheckboxLabel,
  ClearButton,
  CloseIcon,
  Dropdown,
  DropdownEmpty,
  DropdownList,
  DropdownSearchBox,
  DropdownSearchIcon,
  DropdownSearchInput,
  FieldGroup,
  MultiSelectTrigger,
  TriggerText,
} from './styles'
import type { FilterFieldConfig } from './types'

const MultiSelectFilterFieldBase: React.FC<{
  field: FilterFieldConfig
  selected: string[]
  options: string[]
  onChange: (values: string[]) => void
  onFilterCommit?: () => void
}> = ({ field, selected, options, onChange, onFilterCommit }) => {
  const { t, i18n } = useLingui()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const openRef = useRef(open)
  openRef.current = open

  useClickOutside(wrapRef, () => {
    if (openRef.current) {
      onFilterCommit?.()
    }
    setOpen(false)
    setSearch('')
  })

  const selectedSet = useMemo(() => new Set(selected), [selected])

  const optionLabel = useCallback(
    (value: string) =>
      field.formatOption
        ? field.formatOption(value, (descriptor) => i18n._(descriptor))
        : value,
    [field, i18n]
  )

  const filteredOptions = useMemo(() => {
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter((opt) =>
      optionLabel(opt).toLowerCase().includes(lower)
    )
  }, [options, search, optionLabel])

  const toggle = useCallback(
    (value: string) => {
      onChange(
        selectedSet.has(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value]
      )
    },
    [selected, selectedSet, onChange]
  )

  const handleTriggerClick = useCallback(() => {
    setOpen((prevOpen) => {
      if (prevOpen) {
        setSearch('')
        onFilterCommit?.()
      } else {
        requestAnimationFrame(() => searchInputRef.current?.focus())
      }
      return !prevOpen
    })
  }, [onFilterCommit])

  const hasValue = selected.length > 0
  const selectedCount = selected.length
  const displayText = hasValue ? t`${selectedCount} selected` : t`Any`
  const fieldLabel = i18n._(field.label)
  const searchPlaceholderLabel = fieldLabel.toLowerCase()

  return (
    <FieldGroup ref={wrapRef}>
      <Text
        as='span'
        size='xxs'
        weight='semibold'
        color='muted'
        leading='tight'>
        {fieldLabel}
      </Text>
      <MultiSelectTrigger $hasValue={hasValue} onClick={handleTriggerClick}>
        <TriggerText $placeholder={!hasValue}>{displayText}</TriggerText>
        {hasValue && (
          <ClearButton
            onClick={(e) => {
              e.stopPropagation()
              onChange([])
              onFilterCommit?.()
            }}>
            <CloseIcon />
          </ClearButton>
        )}
      </MultiSelectTrigger>
      {open && (
        <Dropdown>
          <DropdownSearchBox>
            <DropdownSearchIcon />
            <DropdownSearchInput
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search ${searchPlaceholderLabel}...`}
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownSearchBox>
          <DropdownList>
            {filteredOptions.length === 0 ? (
              <DropdownEmpty>
                {options.length === 0
                  ? t`No values available`
                  : t`No matches found`}
              </DropdownEmpty>
            ) : (
              filteredOptions.map((opt) => (
                <CheckboxLabel key={opt}>
                  <Checkbox
                    type='checkbox'
                    checked={selectedSet.has(opt)}
                    onChange={() => toggle(opt)}
                  />
                  {optionLabel(opt)}
                </CheckboxLabel>
              ))
            )}
          </DropdownList>
        </Dropdown>
      )}
    </FieldGroup>
  )
}

export const MultiSelectFilterField = memo(MultiSelectFilterFieldBase)
